// A small least-recently-used cache. The time slider revisits recent dates, so a handful of
// entries per structure is enough; the cap bounds memory (a per-hex array is 172–690 KB).

export class Lru<K, V> {
  private readonly map = new Map<K, V>();
  private readonly cap: number;

  constructor(cap: number) {
    this.cap = cap;
  }

  get(key: K): V | undefined {
    const v = this.map.get(key);
    if (v === undefined) {
      return undefined;
    }

    // Re-insert so iteration order tracks recency.
    this.map.delete(key);
    this.map.set(key, v);
    return v;
  }

  set(key: K, value: V): void {
    this.map.delete(key);
    this.map.set(key, value);
    if (this.map.size <= this.cap) {
      return;
    }
    const oldest = this.map.keys().next().value as K;
    this.map.delete(oldest);
  }

  /** Cached value, or compute and store it. */
  use(key: K, make: () => V): V {
    const hit = this.get(key);
    if (hit !== undefined) {
      return hit;
    }
    const v = make();
    this.set(key, v);
    return v;
  }

  clear(): void {
    this.map.clear();
  }
}
